/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import * as ClosestEndPointResolver from 'phenix-web-closest-endpoint-resolver';
import * as _ from 'phenix-web-lodash-light';
import config from 'config';
import fetch from 'services/fetch.service';
import hostService from 'services/host-url.service';
import LoggerFactory from 'services/logger/LoggerFactory';
import {ILogger} from 'services/logger/LoggerInterface';
import userStore from 'services/user-store';
import {auth} from 'services/auth.service';
import {PortalError} from 'utility/error-handler';

class BackendInfoService {
  private window: Window;
  private logger: ILogger;
  public backendUri: string;
  public controlCenterVersion: string;

  init(): void {
    this.backendUri = hostService.getHostUrl();
    this.window = window;
    this.logger = LoggerFactory.getLogger('services/backend-info.service');

    if (!this.backendUri) {
      const protocolWithSlashes = `${this.window.location.protocol || 'http:'}//`;
      const {hostname} = this.window.location;
      const portWithColonIfPresent = `${this.window.location.port ? `:${this.window.location.port}` : ''}`;

      this.backendUri = `${protocolWithSlashes}${hostname}${portWithColonIfPresent}`;
    }

    this.controlCenterVersion = config.controlCenterVersion;
  }

  async getWebsocketUrl(backendURL: string): Promise<string> {
    this.backendUri = backendURL;
    this.window = window;

    this.init();

    return new Promise((resolve: (uri: string) => void, reject): void => {
      this.getEndpoints(3, this.controlCenterVersion).then((endPoints: string[]) => {
        const closestEndPointResolver = new ClosestEndPointResolver({
          logger: this.logger,
          version: this.controlCenterVersion
        }, (errFindingClosestEndpoint: Error, response: {uri?: string}): void => {
          if (errFindingClosestEndpoint) {
            return reject(errFindingClosestEndpoint);
          }

          return resolve(response.uri);
        });

        closestEndPointResolver.resolveAll(endPoints);
      }).catch((errGettingEndpoints: Error) => {
        reject(errGettingEndpoints);
      });
    });
  }

  public async getWebSocketUrlForSDK(): Promise<string> {
    const uri = await this.getWebsocketUrl(this.backendUri);

    return uri.substring(0, uri.indexOf('/ws'));
  }

  public getBackendUriWithPCast(): string {
    if (this.backendUri.split('/').reverse()[0] === 'pcast') {
      return this.backendUri;
    }

    return this.backendUri + '/pcast';
  }

  public async getBackendUri(): Promise<string> {
    if (!this.backendUri) {
      await this.init();
    }

    return this.backendUri;
  }

  private async getEndpoints(maxAttempts: number, version: string): Promise<string[]> {
    try {
      const headers = new Headers();

      headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

      this.logger.info('Fetching end points');

      const response = await fetch.get({
        url: `/pcast/endPoints?version=${version}&_=${_.now()}`,
        headers,
        retryConfig: {maxRetries: maxAttempts}
      });
      const endPoints = response.data.split(',');

      if (!endPoints) {
        throw new PortalError('Failed to discover end points');
      }

      this.logger.info('End points were fetched successfully [%j]', endPoints);

      return endPoints;
    } catch (e) {
      this.logger.error('Failed to resolve an end point', e);

      throw new PortalError('Failed to resolve an end point');
    }
  }
}

export default new BackendInfoService();